/**
 * Pendiente visual de mensajes entrantes hasta abrir la conversación.
 * Persiste aunque Contactos esté desmontado (p. ej. usuario en Dashboard).
 * El dedupe por mensaje vive en `registerPendingIncomingVisual` (también usado desde Contactos).
 * `mergePendingSnapshotOnlyFromPayload` solo actualiza el snapshot (no toca dedupe) para que
 * el handler de Contactos pueda registrar el mismo mensaje después vía `registerPendingIncomingVisual`.
 */

import { getFavoriteRowCanonicalIds } from './favoritePeerId';
import { resolveIncomingVisualPreviewSubtitle } from './incomingVisualPreviewFromPayload';

const seenMessageKeys = new Set();
const pendingContactIds = new Set();
const conversationIdToContactId = new Map();
const lastActivityTsByConversation = new Map();
let previewByContactId = {};

function trimSeenKeys(maxSize = 1200) {
  if (seenMessageKeys.size <= maxSize) return;
  const toRemove = seenMessageKeys.size - maxSize + 200;
  const it = seenMessageKeys.values();
  for (let i = 0; i < toRemove; i += 1) {
    const n = it.next();
    if (n.done) break;
    seenMessageKeys.delete(n.value);
  }
}

function emitPendingVisualChanged() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('archat:pending-incoming-visual-changed', { detail: {} }));
  } catch (_) {
    /* noop */
  }
}

/**
 * @param {string|number|null|undefined} messageKey
 * @returns {boolean}
 */
export function isIncomingMessageKeyConsumed(messageKey) {
  const mk = String(messageKey ?? '').trim();
  return mk !== '' && seenMessageKeys.has(mk);
}

/**
 * Registra pendiente + dedupe por messageKey (Contactos / mismo flujo que antes en markIncomingHighlight).
 * @param {{ contactId: string, conversationId?: string|null, messageKey?: string|null, previewText?: string|null }} p
 * @returns {boolean} false si messageKey duplicado (no actualiza estado local del caller)
 */
export function registerPendingIncomingVisual(p) {
  const cidContact = String(p?.contactId ?? '').trim();
  if (cidContact === '') return false;
  const mk = String(p?.messageKey ?? '').trim();
  if (mk !== '') {
    if (seenMessageKeys.has(mk)) return false;
    seenMessageKeys.add(mk);
    trimSeenKeys();
  }
  const conv = String(p?.conversationId ?? '').trim();
  if (conv !== '') {
    conversationIdToContactId.set(conv, cidContact);
    lastActivityTsByConversation.set(conv, Date.now());
  }
  pendingContactIds.add(cidContact);
  if (p?.previewText != null && String(p.previewText).trim() !== '') {
    previewByContactId = { ...previewByContactId, [cidContact]: String(p.previewText).trim() };
  }
  emitPendingVisualChanged();
  return true;
}

/**
 * Actualiza snapshot global sin consumir dedupe (MessagesProvider / cualquier vista desmontada).
 * Debe ejecutarse antes que los handlers de Contactos en `publishIncomingChatMessage` (orden de suscripción).
 * @param {object} data
 * @param {string} activeConversationIdRaw
 * @param {number|string} currentUserIdNum
 */
export function mergePendingSnapshotOnlyFromPayload(data, activeConversationIdRaw, currentUserIdNum) {
  if (!data || currentUserIdNum == null || currentUserIdNum === '') return;
  const remitenteId =
    data?.remitente_id || data?.emisor_id || data?.usuario_id || data?.emisor || data?.sender_id;
  if (!remitenteId) return;
  if (parseInt(String(remitenteId), 10) === parseInt(String(currentUserIdNum), 10)) return;

  const rowForIds = {
    otro_usuario_id: remitenteId,
    id: remitenteId,
    usuario_id: remitenteId,
  };
  const { peerIdStr } = getFavoriteRowCanonicalIds(rowForIds, currentUserIdNum, 0);
  const contactId =
    peerIdStr && String(peerIdStr).trim() !== '' ? String(peerIdStr).trim() : String(remitenteId);
  const conversationId =
    data?.conversacion_id ?? data?.conversation_id ?? data?.conversationId ?? data?.cid ?? null;
  const msgCid = String(
    conversationId ?? data?.conversacion_id ?? data?.conversation_id ?? ''
  ).trim();
  const activeCid = String(activeConversationIdRaw ?? '').trim();
  const isActiveThread = msgCid !== '' && activeCid !== '' && msgCid === activeCid;
  if (isActiveThread) return;

  const conv = String(conversationId ?? '').trim();
  if (conv !== '') {
    conversationIdToContactId.set(conv, contactId);
    lastActivityTsByConversation.set(conv, Date.now());
  }
  pendingContactIds.add(contactId);
  const visualSubtitle = resolveIncomingVisualPreviewSubtitle(data);
  if (visualSubtitle != null && String(visualSubtitle).trim() !== '') {
    previewByContactId = { ...previewByContactId, [contactId]: String(visualSubtitle).trim() };
  }
  emitPendingVisualChanged();
}

export function clearPendingIncomingForContact(contactIdRaw) {
  const cid = String(contactIdRaw ?? '').trim();
  if (cid === '') return;
  let changed = pendingContactIds.delete(cid);
  if (previewByContactId[cid] !== undefined) {
    const next = { ...previewByContactId };
    delete next[cid];
    previewByContactId = next;
    changed = true;
  }
  for (const [conv, peer] of [...conversationIdToContactId.entries()]) {
    if (String(peer) === cid) {
      conversationIdToContactId.delete(conv);
      lastActivityTsByConversation.delete(conv);
      changed = true;
    }
  }
  if (changed) emitPendingVisualChanged();
}

/**
 * @param {string|number|null|undefined} conversationIdRaw
 */
export function clearPendingIncomingForConversation(conversationIdRaw) {
  const conv = String(conversationIdRaw ?? '').trim();
  if (conv === '' || conv === 'null' || conv === 'undefined') return;
  const contactId = conversationIdToContactId.get(conv);
  if (!contactId) return;
  conversationIdToContactId.delete(conv);
  lastActivityTsByConversation.delete(conv);
  pendingContactIds.delete(contactId);
  if (previewByContactId[contactId] !== undefined) {
    const next = { ...previewByContactId };
    delete next[contactId];
    previewByContactId = next;
  }
  emitPendingVisualChanged();
}

export function peekPendingIncomingSnapshot() {
  return {
    contactIds: new Set(pendingContactIds),
    previews: { ...previewByContactId },
    conversationToContact: new Map(conversationIdToContactId),
  };
}

/**
 * Filas sintéticas para el dashboard cuando el API aún no refleja el no leído.
 * @param {object[]} strictRows
 * @param {string|number} userId
 * @returns {object[]}
 */
export function buildSyntheticDashboardRowsFromPending(strictRows, userId) {
  const have = new Set();
  (Array.isArray(strictRows) ? strictRows : []).forEach((r) => {
    const id = r?.conversacion_id ?? r?.conversation_id;
    if (id != null && String(id).trim() !== '') have.add(String(id).trim());
  });
  const out = [];
  const uNum = userId != null && String(userId).trim() !== '' ? Number(userId) : null;

  conversationIdToContactId.forEach((contactId, conv) => {
    const convStr = String(conv).trim();
    if (convStr === '' || have.has(convStr)) return;
    const preview = String(previewByContactId[contactId] ?? '').trim();
    if (preview === '') return;
    const ts = lastActivityTsByConversation.get(convStr) ?? Date.now();
    const peerNum = Number(contactId);
    const peerId = Number.isFinite(peerNum) ? peerNum : contactId;
    const rem =
      uNum != null && Number.isFinite(uNum) && String(peerId) === String(uNum) ? null : peerId;
    const convIdVal = Number.isFinite(Number(convStr)) ? Number(convStr) : convStr;
    out.push({
      __syntheticPending: true,
      conversacion_id: convIdVal,
      conversation_id: convIdVal,
      contacto_peer_id: peerId,
      otro_usuario_id: peerId,
      contacto_nombre: '\u2014',
      ultimo_sin_leer_para_mi: 1,
      unread_evidenced_by: 'estados_mensajes',
      em_estado_para_mi: 'entregado',
      remitente_id: rem,
      mensaje: preview,
      fecha_envio: new Date(ts).toISOString(),
      tipo: 'texto',
    });
  });
  return out;
}

/**
 * @param {object[]} strictRows
 * @param {string|number} userId
 * @returns {object[]}
 */
export function mergeDashboardStrictWithSyntheticPending(strictRows, userId) {
  const strict = Array.isArray(strictRows) ? strictRows : [];
  const synthetic = buildSyntheticDashboardRowsFromPending(strict, userId);
  const byCid = new Map();
  strict.forEach((r) => {
    const k = String(r?.conversacion_id ?? r?.conversation_id ?? '').trim();
    if (k) byCid.set(k, r);
  });
  synthetic.forEach((r) => {
    const k = String(r?.conversacion_id ?? r?.conversation_id ?? '').trim();
    if (k && !byCid.has(k)) byCid.set(k, r);
  });
  const merged = [...byCid.values()];
  merged.sort((a, b) => {
    const ta = Date.parse(String(a?.fecha_envio ?? a?.fecha ?? 0)) || 0;
    const tb = Date.parse(String(b?.fecha_envio ?? b?.fecha ?? 0)) || 0;
    return tb - ta;
  });
  return merged;
}
